import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { getBridgeScopeShipmentId } from '#admin/helpers.ts';
import type { RootState } from '#admin/store.ts';

type Props = {
  ifAuthed: boolean;
  element: React.ReactElement<any, any>;
  elseTo: string;
};

export const AuthGate: React.FC<Props> = ({ ifAuthed, element, elseTo }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = useSelector((state: RootState) => state.app.token);
  const isAuthenticated = !!token;
  const isBridgeAuthRoute = location.pathname === '/auth' && searchParams.has('token');
  const bridgeScopeShipmentId = getBridgeScopeShipmentId(token);
  const signedTimestamp = searchParams.get('timestamp');
  const signedSignature = searchParams.get('signature');
  const shipmentMatch = location.pathname.match(/^\/shipment\/([^/]+)$/);
  const hasSignedShipmentParams = Boolean(shipmentMatch?.[1] && signedTimestamp && signedSignature);
  const isScopedShipmentRoute = Boolean(shipmentMatch && shipmentMatch[1] === bridgeScopeShipmentId);
  const bridgeRedirectPath = bridgeScopeShipmentId ? `/shipment/${bridgeScopeShipmentId}` : '/';

  if (ifAuthed && !isAuthenticated && hasSignedShipmentParams) {
    const shipmentId = shipmentMatch?.[1];
    const externalBridgeAuthURL = `${import.meta.env.VITE_API_URL?.replace(/\/$/, '')}/auth/bridge/mycryopdp/shipment/${shipmentId}?timestamp=${encodeURIComponent(
      signedTimestamp || '',
    )}&signature=${encodeURIComponent(signedSignature || '')}`;
    window.location.href = externalBridgeAuthURL;
    return null;
  }

  if (ifAuthed && isAuthenticated && bridgeScopeShipmentId && !isScopedShipmentRoute) {
    return <Navigate to={bridgeRedirectPath} replace />;
  }

  if (ifAuthed) {
    if (isAuthenticated) return element;
    else return <Navigate to={elseTo} />;
  } else {
    if (!isAuthenticated || isBridgeAuthRoute) return element;
    else return <Navigate to={elseTo} />;
  }
};
